import { EmailTemplate, EmailTemplateData } from '../types/sendgrid';

const TEMPLATE_IDS: Record<EmailTemplate, string> = {
  'team-verification': 'd-42fdc836c7414bd4b88a7fd35108c31f',
  'league-invitation': 'd-42fdc836c7414bd4b88a7fd35108c31f',
  'social-media-submission-verification-admin': 'd-fe2c08e6fe7446b28eab8d944db0e3f2',
  'social-media-submission-verification-user': 'd-c9addc81630445ae851fb39053116b0e',
  'signup-email-verification': 'd-7b795b85a952470cbc3ac34075a60841',
  'forgot-password-email': 'd-cd518343841440d49c924c5e394001c5',
  'new-league-follower-to-commissioner':'d-25bc14a4e17d44e395dabd4309ae8e91',
};

export async function sendTemplatedEmail(
  template: EmailTemplate,
  config: {
    to: string;
    subject: string;
    templateData: Omit<EmailTemplateData, 'subject'>;
  }
): Promise<boolean> {
  console.log('Starting sendTemplatedEmail with config:', {
    template,
    to: config.to,
    subject: config.subject,
    templateDataKeys: Object.keys(config.templateData)
  });

  try {
    // Simplified message structure that matches SendGrid's expectations
    const msg = {
      to: config.to,
      from: {
        email: 'noreply@fantasyloser.com',
        name: 'Fantasy Loser'
      },
      subject: config.subject,
      template_id: TEMPLATE_IDS[template],
      dynamicTemplateData: {
        ...config.templateData,
        subject: config.subject
      }
    };

    console.log('Sending message to Netlify function:', msg); // Debug log

    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(msg)
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('SendGrid API Error Response:', responseData);
      throw new Error(
        `SendGrid API error (${response.status}): ${responseData.error || responseData.message || 'Unknown error'}`
      );
    }

    console.log('SendGrid API Success Response:', responseData); // Debug log
    return true;

  } catch (error) {
    console.error('SendGrid Service Error:', {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      config: {
        template,
        to: config.to,
        subject: config.subject
      }
    });

    throw error;
  }
}

export async function sendVerificationEmail(
  email: string,
  code: string,
  teamName: string,
  leagueName: string
): Promise<boolean> {
  console.log('Sending verification email:', {
    email,
    teamName,
    leagueName,
    codeLength: code.length
  });

  try {
    return await sendTemplatedEmail('team-verification', {
      to: email,
      subject: `Verify Your ${leagueName} Team`,
      templateData: {
        verificationCode: code,
        teamName: teamName || 'Unknown Team',
        leagueName,
        expiryMinutes: 15
      }
    });
  } catch (error) {
    console.error('Failed to send verification email:', {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      email,
      teamName,
      leagueName
    });
    throw error;
  }
}

export async function sendLeagueInvitation(
  email: string,
  leagueName: string,
  inviteLink: string,
  commissionerName: string,
  leagueYear: string
): Promise<boolean> {
  console.log('Sending league invitation:', {
    email,
    leagueName,
    commissionerName,
    leagueYear,
    inviteLinkDomain: new URL(inviteLink).hostname
  });

  try {
    return await sendTemplatedEmail('league-invitation', {
      to: email,
      subject: `Join ${leagueName} on Fantasy Loser`,
      templateData: {
        recipientEmail: email,
        leagueName,
        inviteLink,
        commissionerName,
        leagueYear
      }
    });
  } catch (error) {
    console.error('Failed to send league invitation:', {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      email,
      leagueName
    });
    throw error;
  }
}

export async function sendSocialMediaSubmissionReviewEmail(
  email: string,
  contentTitle: string,
  firstName: string,
  url: string,
  isAdminEmail: Boolean,
): Promise<boolean> {
  try {
    return await sendTemplatedEmail(isAdminEmail ? 'social-media-submission-verification-admin' : 'social-media-submission-verification-user', {
      to: isAdminEmail ? process.env.ADMIN_EMAIL || 'engineering@fantasyloser.com' : email,
      subject: isAdminEmail ? `A new clip has been submitted! | ${contentTitle}` : `Thanks for the clip!`,
      templateData: {
        email: email,
        contentTitle: contentTitle,
        firstName: firstName,
        url: url
      }
    });
  } catch (error) {
    console.error('Failed to send verification email:', {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      email,
      contentTitle,
      url,
      firstName
    });
    throw error;
  }
};

export async function sendSignUpVerificationEmail(
  email: string,
  firstName: string,
  url: string,
): Promise<boolean> {
  try {
    return await sendTemplatedEmail('signup-email-verification', {
      to: email,
      subject: 'Verify your Fantasy Loser account',
      templateData: {
        firstName: firstName,
        url: url
      }
    });
  } catch (error) {
    console.error('Failed to send verification email:', {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      email,
      url,
      firstName
    });
    throw error;
  }
};

export async function sendEmailOnFollowingLeague(
  email: string,
  leagueName: string,
  commissionerFirstName: string,
  userFullName: string,
): Promise<boolean> {
  try {
    return await sendTemplatedEmail('new-league-follower-to-commissioner', {
      to: email,
      subject: `Someone has started following ${leagueName}`,
      templateData: {
        email: email,
        leagueName: leagueName,
        commissionerFirstName: commissionerFirstName,
        userFullName: userFullName,
      }
    });
  } catch (error) {
    console.error('Failed to send follow league email to commissioner:', {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      email,
      leagueName,
      commissionerFirstName,
      userFullName,
    });
    throw error;
  }
};